import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { portfolioSlides } from '@/constants/data';

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0.6 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0.6 }),
};

const PortfolioFrame = () => {
  const [[index, direction], setSlide] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const [zoom, setZoom] = useState(false);

  const go = (newIndex, dir) => setSlide([(newIndex + portfolioSlides.length) % portfolioSlides.length, dir]);

  useEffect(() => {
    if (paused || zoom) return;
    const t = setInterval(() => setSlide(([i]) => [(i + 1) % portfolioSlides.length, 1]), 4000);
    return () => clearInterval(t);
  }, [paused, zoom]);

  const prev = () => go(index - 1, -1);
  const next = () => go(index + 1, 1);

  return (
    <div data-testid="portfolio-frame">
      {/* Moldura ajustada à proporção da arte */}
      <div className="flex justify-center"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="relative h-[420px] sm:h-[500px] lg:h-[540px] overflow-hidden rounded-xl cursor-zoom-in" onClick={() => setZoom(true)} data-testid="portfolio-zoom-trigger">
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.img key={index} custom={direction} src={portfolioSlides[index].image} alt={portfolioSlides[index].title}
              variants={slideVariants} initial="enter" animate="center" exit="exit"
              transition={{ x: { type: 'tween', duration: 0.45, ease: [0.32, 0.72, 0, 1] }, opacity: { duration: 0.3 } }}
              className="h-full w-auto max-w-full object-contain rounded-xl border border-white/10" loading="lazy" />
          </AnimatePresence>
        </div>
      </div>

      {/* Título e controles fora da imagem */}
      <div className="flex items-center justify-center mt-4 gap-4">
        <button onClick={prev} data-testid="frame-prev-btn"
          className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 flex items-center justify-center transition-colors flex-shrink-0">
          <ChevronLeft size={16} />
        </button>
        <div className="text-center min-w-0 w-56 sm:w-72">
          <p className="font-heading text-white text-sm sm:text-base uppercase tracking-wide truncate">{portfolioSlides[index].title}</p>
          <div className="flex justify-center gap-1 mt-2">
            {portfolioSlides.map((_, i) => (
              <button key={i} onClick={() => go(i, i > index ? 1 : -1)} data-testid={`frame-dot-${i}`}
                className={`h-1 rounded-full transition-all duration-300 ${i === index ? 'bg-[#00A8E8] w-4' : 'bg-white/25 w-1'}`} />
            ))}
          </div>
        </div>
        <button onClick={next} data-testid="frame-next-btn"
          className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 flex items-center justify-center transition-colors flex-shrink-0">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Lightbox - ampliar ao clicar */}
      {zoom && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="portfolio-lightbox">
          <img src={portfolioSlides[index].image} alt={portfolioSlides[index].title}
            className="max-h-[92vh] max-w-full object-contain rounded-lg" />
          <button onClick={() => setZoom(false)} data-testid="lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
};

const InstaVideo = ({ postUrl, cover, title }) => (
  <a href={postUrl} target="_blank" rel="noopener noreferrer" title={title}
    className="group relative block rounded-xl overflow-hidden border border-white/10" data-testid="insta-video-card">
    <img src={cover} alt={title} className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-xl transition-all group-hover:scale-110">
        <Play size={26} className="text-[#0A2463] ml-1" fill="#0A2463" />
      </div>
    </div>
    <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
      <Instagram size={15} className="text-white" />
    </div>
  </a>
);

const VideoDestaqueSection = () => (
  <section className="py-16 sm:py-20 lg:py-28 bg-[#0A2463]" data-testid="video-destaque-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Linha 1: título ao lado do portfólio em moldura */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="lg:col-span-2 text-center lg:text-left">
          <p className="text-[#00A8E8] text-xs font-bold uppercase tracking-[0.25em] mb-3">Portfólio</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-tight">Paixão por tecnologia.<br />Excelência na entrega.</h2>
          <p className="text-white/60 text-sm sm:text-base font-body mt-4 max-w-md mx-auto lg:mx-0">
            Conheça nosso trabalho.
          </p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="lg:col-span-3">
          <PortfolioFrame />
        </motion.div>
      </div>

      {/* Linha 2: vídeos do Instagram com capas próprias */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <InstaVideo postUrl="https://www.instagram.com/p/DPxAfABDUk7/" cover="/covers/video1.jpg" title="Grupo Tomaselli - Amazon On" />
        <InstaVideo postUrl="https://www.instagram.com/p/DGRPhSIPheV/" cover="/covers/video2.jpg" title="Grupo Tomaselli - Totens Interativos" />
        <InstaVideo postUrl="https://www.instagram.com/p/DRN4FTfDcbX/" cover="/covers/video3.jpg" title="Grupo Tomaselli - Transmissão ao Vivo" />
      </motion.div>

      <div className="text-center mt-8">
        <a href="https://www.instagram.com/grupotomaselli" target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-btn"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-lg">
          <Instagram size={18} /> Siga @grupotomaselli
        </a>
      </div>
    </div>
  </section>
);

export default VideoDestaqueSection;
